import React from 'react';
import { Form, InputNumber, Row, Col, Select, Tooltip, Switch, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ProductType } from '../../../../../utils/fbaFeeData';
import { DEFAULT_VALUES } from '../../../constants/pricingConstants';

const { Option } = Select;

interface RateParametersProps {
    productType: ProductType;
    setProductType: (type: ProductType) => void;
    referralRate: number;
    setReferralRate: (rate: number) => void;
    returnRate: number;
    setReturnRate: (rate: number) => void;
    otherCostRate: number;
    setOtherCostRate: (rate: number) => void;
    // FBA Props
    fbaFee: number;
    setFbaFee: (fee: number) => void;
    fbaTierName: string;
    isPeakSeason: boolean;
    setIsPeakSeason: (isPeak: boolean) => void;
    currentStorageFee: number;
    storageFee: { jan_sep: number; oct_dec: number };
    volumeWeight: number;
    chargeableWeight: number;
}

export const RateParameters: React.FC<RateParametersProps> = ({
    productType,
    setProductType,
    referralRate,
    setReferralRate,
    returnRate,
    setReturnRate,
    otherCostRate,
    setOtherCostRate,
    fbaFee,
    setFbaFee,
    fbaTierName,
    isPeakSeason,
    setIsPeakSeason,
    currentStorageFee,
    storageFee,
    volumeWeight,
    chargeableWeight,
}) => {
    return (
        <>
            <Row gutter={[12, 12]}>
                <Col span={8}>
                    <Form.Item label="产品类型" style={{ marginBottom: 12 }}>
                        <Select value={productType} onChange={setProductType} style={{ width: '100%' }} size="small">
                            <Option value="standard">标准商品</Option>
                            <Option value="apparel">服装鞋靴</Option>
                            <Option value="dangerous_goods">危险品</Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="佣金率(%)" style={{ marginBottom: 12 }}>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={referralRate}
                            onChange={(v) => setReferralRate(v || DEFAULT_VALUES.REFERRAL_RATE)}
                            precision={1}
                            size="small"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item label="退货率(%)" style={{ marginBottom: 12 }}>
                        <InputNumber
                            style={{ width: '100%' }}
                            value={returnRate}
                            onChange={(v) => setReturnRate(v || 0)}
                            precision={1}
                            min={0}
                            max={100}
                            size="small"
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={[12, 12]}>
                <Col span={8}>
                    <Form.Item
                        label={<Tooltip title="其他成本率（包括市场推广、仓储等杂费）"><span>其他成本率(%) <InfoCircleOutlined /></span></Tooltip>}
                        style={{ marginBottom: 12 }}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            value={otherCostRate}
                            onChange={(v) => setOtherCostRate(v || DEFAULT_VALUES.OTHER_COST_RATE)}
                            precision={1}
                            min={0}
                            max={100}
                            size="small"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label={
                            <Space>
                                FBA费($)
                                {fbaTierName && (
                                    <Tooltip title={fbaTierName}>
                                        <InfoCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                                    </Tooltip>
                                )}
                                <Space size={4}>
                                    <span style={{ fontSize: 12, color: isPeakSeason ? '#faad14' : '#999' }}>旺季</span>
                                    <Switch
                                        size="small"
                                        checked={isPeakSeason}
                                        onChange={setIsPeakSeason}
                                        style={{ transform: 'scale(0.8)' }}
                                    />
                                </Space>
                            </Space>
                        }
                        style={{ marginBottom: 12 }}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            value={fbaFee}
                            onChange={(v) => setFbaFee(v || 0)}
                            prefix="$"
                            precision={2}
                            size="small"
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label={
                            <Space>
                                仓储费($/月)
                                <Tooltip title={`淡季(1-9月): $${storageFee.jan_sep.toFixed(2)}, 旺季(10-12月): $${storageFee.oct_dec.toFixed(2)}`}>
                                    <InfoCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                                </Tooltip>
                            </Space>
                        }
                        style={{ marginBottom: 12 }}
                    >
                        <InputNumber
                            style={{ width: '100%', background: '#f5f5f5' }}
                            value={currentStorageFee}
                            readOnly
                            prefix="$"
                            precision={2}
                            size="small"
                        />
                    </Form.Item>
                </Col>
            </Row>

            <div style={{
                background: '#f0f5ff',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 12,
                color: '#666',
                marginTop: 4,
                marginBottom: 12
            }}>
                <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
                    <span>体积重: <b>{volumeWeight.toFixed(2)}</b> kg</span>
                    <span>计费重: <b>{chargeableWeight.toFixed(2)}</b> kg</span>
                </Space>
            </div>
        </>
    );
};
